import type { ReactNode } from "react";

import { XIcon } from "lucide-react";

import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";

import { EMPTY_FILTERS, type RawFilters } from "../types";

type Props = {
  filters: RawFilters;
  onChange: (patch: Partial<RawFilters>) => void;
};

const FilterField = ({
  id,
  label,
  children,
}: {
  id?: string;
  label: string;
  children: ReactNode;
}) => {
  const labelClassName = "text-xs font-medium text-foreground/60";
  return (
    <div className="flex flex-col gap-1">
      {/* No `id` means this isn't a single form control's label (e.g. the
          "With Docs" group heading, which has its own separately-associated
          checkbox label below) -- render a plain span, not a dangling
          `<label>` with no `for` target. */}
      {id ? (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      ) : (
        <span className={labelClassName}>{label}</span>
      )}
      {children}
    </div>
  );
};

export const FilterBar = ({ filters, onChange }: Props) => {
  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value !== EMPTY_FILTERS[key as keyof RawFilters]
  );

  return (
    <div className="grid grid-cols-2 gap-3 rounded-md border p-4 md:grid-cols-4 lg:grid-cols-8">
      <FilterField id="booking-id-filter" label="Booking ID">
        <Input
          id="booking-id-filter"
          type="number"
          inputMode="numeric"
          placeholder="e.g. 114696"
          value={filters.bookingId}
          onChange={(e) => onChange({ bookingId: e.target.value })}
          className="h-9"
        />
      </FilterField>

      <FilterField id="medical-examiner-filter" label="Medical Examiner">
        <Input
          id="medical-examiner-filter"
          placeholder="Search…"
          value={filters.medicalExaminer}
          onChange={(e) => onChange({ medicalExaminer: e.target.value })}
          className="h-9"
        />
      </FilterField>

      <FilterField id="referrer-filter" label="Referrer">
        <Input
          id="referrer-filter"
          placeholder="Search…"
          value={filters.referrer}
          onChange={(e) => onChange({ referrer: e.target.value })}
          className="h-9"
        />
      </FilterField>

      <FilterField id="examinee-filter" label="Examinee">
        <Input
          id="examinee-filter"
          placeholder="Search…"
          value={filters.examinee}
          onChange={(e) => onChange({ examinee: e.target.value })}
          className="h-9"
        />
      </FilterField>

      <FilterField id="appointment-date-filter" label="Appointment Date">
        <Input
          id="appointment-date-filter"
          type="date"
          value={filters.appointmentDate}
          onChange={(e) => onChange({ appointmentDate: e.target.value })}
          className="h-9"
        />
      </FilterField>

      <FilterField id="secure-and-switch-filter" label="Secure&Switch">
        <Select
          value={filters.secureAndSwitch}
          onValueChange={(v) =>
            onChange({ secureAndSwitch: v as RawFilters["secureAndSwitch"] })
          }
        >
          <SelectTrigger id="secure-and-switch-filter" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField id="match-status-filter" label="Match">
        <Select
          value={filters.matchStatus}
          onValueChange={(v) =>
            onChange({ matchStatus: v as RawFilters["matchStatus"] })
          }
        >
          <SelectTrigger id="match-status-filter" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="MATCHED_BY_ID">MATCHED_BY_ID</SelectItem>
            <SelectItem value="MATCHED_BY_EMAIL">MATCHED_BY_EMAIL</SelectItem>
            <SelectItem value="UNMATCHED">UNMATCHED</SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="With Docs">
        <div className="flex h-9 items-center gap-2">
          <Checkbox
            id="with-docs-filter"
            checked={filters.withDocs}
            onCheckedChange={(checked) => onChange({ withDocs: checked === true })}
          />
          <label htmlFor="with-docs-filter" className="text-sm text-foreground/70">
            Has documents
          </label>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Clear filters"
              className="ml-auto"
              onClick={() => onChange(EMPTY_FILTERS)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </FilterField>
    </div>
  );
};
